import { useQuery } from '@tanstack/react-query'

import { consulta, pedir } from '../../api/nucleo'
import type { Lista } from '../../api/tipos'
import { Selector, clases } from '../../componentes/ui'
import type { Campo } from '../esquema'
import { useFormulario, useValor } from '../Formulario'
import { leer } from '../rutas'
import { claseDeEntrada, Envoltura } from './Controles'

/**
 * Los controles de los campos de datos (plan del CMS dinámico §5.3) que el motor no tenía de
 * fábrica: color, hora, fecha con hora, casillas, lugar oficial y ubicación en el mapa; la
 * plantilla de una página; y el valor de una regla de ubicación de un grupo de campos. El
 * núcleo los pide con `custom.panel.control`, y lo que se guarda lo vuelve a validar él.
 */
type Props = { campo: Campo; ruta: string; soloLectura: boolean }
type Fila = Record<string, unknown>

const describe = (ruta: string, campo: Campo, error?: string) => (error ? `${ruta}-error` : campo.descripcion ? `${ruta}-ayuda` : undefined)
const texto = (v: unknown, alternativa = ''): string => (typeof v === 'string' ? v : alternativa)

/** `#rrggbb`: el selector del navegador y, al lado, el código para quien lo tiene apuntado. */
export const Color = ({ campo, ruta, soloLectura }: Props) => {
  const { valor, cambiar, error } = useValor<string>(ruta)
  const actual = valor ?? ''
  const hex = /^#[0-9a-f]{6}$/i.test(actual) ? actual : '#000000'
  return (
    <Envoltura campo={campo} error={error} ruta={ruta}>
      <div className="flex items-center gap-2">
        <input aria-label={`${campo.etiqueta}: elegir color`} className="size-9 shrink-0 cursor-pointer rounded-boton border border-linea bg-papel p-0.5" disabled={soloLectura} onChange={(e) => cambiar(e.target.value.toLowerCase())} type="color" value={hex} />
        <input
          aria-describedby={describe(ruta, campo, error)}
          aria-invalid={Boolean(error)}
          className={clases(claseDeEntrada(error), 'font-mono text-[13px]')}
          disabled={soloLectura}
          id={ruta}
          onChange={(e) => cambiar(e.target.value.trim().toLowerCase() || undefined)}
          placeholder="#1a2b3c"
          value={actual}
        />
      </div>
    </Envoltura>
  )
}

/** `HH:MM`, en 24 horas. */
export const Hora = ({ campo, ruta, soloLectura }: Props) => {
  const { valor, cambiar, error } = useValor<string>(ruta)
  return (
    <Envoltura campo={campo} error={error} ruta={ruta}>
      <input aria-describedby={describe(ruta, campo, error)} aria-invalid={Boolean(error)} className={claseDeEntrada(error)} disabled={soloLectura} id={ruta} onChange={(e) => cambiar(e.target.value || undefined)} type="time" value={valor ?? ''} />
    </Envoltura>
  )
}

const aLocal = (iso: string): string => {
  const fecha = new Date(iso)
  if (Number.isNaN(fecha.getTime())) return ''
  const dos = (n: number) => String(n).padStart(2, '0')
  return `${fecha.getFullYear()}-${dos(fecha.getMonth() + 1)}-${dos(fecha.getDate())}T${dos(fecha.getHours())}:${dos(fecha.getMinutes())}`
}

/** Se guarda en ISO 8601 (UTC); se enseña y se escribe en la hora de quien edita. */
export const FechaHora = ({ campo, ruta, soloLectura }: Props) => {
  const { valor, cambiar, error } = useValor<string>(ruta)
  return (
    <Envoltura campo={campo} error={error} ruta={ruta}>
      <input
        aria-describedby={describe(ruta, campo, error)}
        aria-invalid={Boolean(error)}
        className={claseDeEntrada(error)}
        disabled={soloLectura}
        id={ruta}
        onChange={(e) => {
          const local = e.target.value
          const fecha = local ? new Date(local) : null
          cambiar(fecha && !Number.isNaN(fecha.getTime()) ? fecha.toISOString() : undefined)
        }}
        type="datetime-local"
        value={valor ? aLocal(valor) : ''}
      />
    </Envoltura>
  )
}

/** Varias opciones, todas a la vista. */
export const Casillas = ({ campo, ruta, soloLectura }: Props) => {
  const { valor, cambiar, error } = useValor<string[]>(ruta)
  const elegidas = Array.isArray(valor) ? valor : []
  return (
    <Envoltura campo={campo} error={error} ruta={ruta}>
      <div className="flex flex-wrap gap-x-4 gap-y-2" id={ruta} role="group" aria-describedby={describe(ruta, campo, error)}>
        {(campo.opciones ?? []).map((o) => (
          <label className="flex cursor-pointer items-center gap-2 text-sm" key={o.valor}>
            <input
              checked={elegidas.includes(o.valor)}
              className="size-4 accent-violeta"
              disabled={soloLectura}
              onChange={(e) => cambiar(e.target.checked ? [...elegidas, o.valor] : elegidas.filter((v) => v !== o.valor))}
              type="checkbox"
            />
            {o.etiqueta}
          </label>
        ))}
      </div>
    </Envoltura>
  )
}

// --- Lugar oficial ----------------------------------------------------------------------------

type Lugares = { paises?: Array<{ codigo: string; nombre: string }>; lugares: Array<{ codigo: string; nombre: string; hoja?: boolean; ultimo?: boolean }>; nombreDelNivel?: null | string }
type ValorDeLugar = { pais: string; codigo: null | string; camino?: string; texto?: string }

const nombreDePais = (codigo: string): string => {
  try {
    return new Intl.DisplayNames(['es'], { type: 'region' }).of(codigo) ?? codigo
  } catch {
    return codigo
  }
}

const NivelDeLugar = ({ sitio, pais, padre, elegido, poner, id }: { elegido: string; id: string; padre: null | string; pais: string; poner: (codigo: string, final: boolean) => void; sitio: number }) => {
  const { data, isError, isPending } = useQuery({
    queryKey: ['lugares-de-la-web', sitio, pais, padre],
    queryFn: () => pedir<Lugares>(`/publico/lugares${consulta({ sitio, pais, ...(padre ? { padre } : {}) })}`),
    retry: false,
    staleTime: 10 * 60_000,
  })
  const nombre = data?.nombreDelNivel || 'Lugar'
  if (!isPending && !isError && (data?.lugares ?? []).length === 0) return null
  return (
    <Selector
      aria-label={nombre}
      disabled={isPending || isError}
      id={id}
      onChange={(e) => {
        const lugar = data?.lugares.find((l) => l.codigo === e.target.value)
        poner(e.target.value, Boolean(lugar?.hoja || lugar?.ultimo))
      }}
      value={elegido}
    >
      <option value="">{isPending ? 'Cargando…' : isError ? 'No se ha podido cargar' : nombre}</option>
      {(data?.lugares ?? []).map((l) => (
        <option key={l.codigo} value={l.codigo}>
          {l.nombre}
        </option>
      ))}
    </Selector>
  )
}

/**
 * País, departamento, provincia… con los lugares oficiales que da el núcleo (`/publico/lugares`,
 * el mismo que usa el carrito). Se guarda `{ pais, codigo }`; el camino lo escribe el servidor.
 * El código elegido es el del último nivel; los de arriba se deducen al cargar la cadena.
 */
export const Lugar = ({ campo, ruta, soloLectura }: Props) => {
  const { sitio } = useFormulario()
  const { valor, cambiar, error } = useValor<ValorDeLugar>(ruta)
  const paisesAdmitidos = Array.isArray(campo.control?.paises) ? (campo.control.paises as string[]) : []
  const nivelPedido = texto(campo.control?.nivel, 'ultimo')
  const { data: delSitio } = useQuery({
    queryKey: ['paises-de-la-web', sitio],
    queryFn: () => pedir<Lugares>(`/publico/lugares${consulta({ sitio: sitio ?? 0 })}`),
    enabled: sitio !== null && paisesAdmitidos.length !== 1,
    retry: false,
    staleTime: 10 * 60_000,
  })
  const paises = (paisesAdmitidos.length > 0 ? paisesAdmitidos.map((c) => ({ codigo: c, nombre: nombreDePais(c) })) : (delSitio?.paises ?? [])).sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'))
  const pais = valor?.pais ?? (paisesAdmitidos.length === 1 ? paisesAdmitidos[0]! : '')
  // Los niveles elegidos, de arriba abajo: el código guardado es el último; los de arriba vienen con `padre`.
  const cadena = Array.isArray((valor as Fila | undefined)?.cadena) ? ((valor as Fila).cadena as string[]) : valor?.codigo ? [valor.codigo] : []
  const tope = nivelPedido === 'primero' ? 1 : nivelPedido === 'segundo' ? 2 : 10

  if (sitio === null) return null
  return (
    <Envoltura campo={campo} error={error} ruta={ruta}>
      <div className="grid gap-2 sm:grid-cols-2" id={ruta} aria-describedby={describe(ruta, campo, error)}>
        {paisesAdmitidos.length !== 1 && (
          <Selector aria-label="País" disabled={soloLectura} onChange={(e) => cambiar(e.target.value ? { pais: e.target.value, codigo: null } : undefined)} value={pais}>
            <option value="">País</option>
            {paises.map((p) => (
              <option key={p.codigo} value={p.codigo}>
                {p.nombre}
              </option>
            ))}
          </Selector>
        )}
        {pais &&
          !soloLectura &&
          Array.from({ length: Math.min(tope, cadena.length + 1) }).map((_, nivel) => (
            <NivelDeLugar
              elegido={cadena[nivel] ?? ''}
              id={`${ruta}-nivel-${nivel}`}
              key={`${pais}-${nivel}-${cadena[nivel - 1] ?? ''}`}
              padre={nivel === 0 ? null : (cadena[nivel - 1] ?? null)}
              pais={pais}
              poner={(codigo, final) => {
                const nueva = codigo ? [...cadena.slice(0, nivel), codigo] : cadena.slice(0, nivel)
                const ultimo = nueva[nueva.length - 1] ?? null
                cambiar({ pais, codigo: ultimo, ...(nueva.length > 0 ? { cadena: nueva } : {}), ...(final ? {} : {}) } as ValorDeLugar)
              }}
              sitio={sitio}
            />
          ))}
        {valor?.camino && <p className="text-xs text-tenue sm:col-span-2">{valor.camino}</p>}
      </div>
    </Envoltura>
  )
}

/** Una dirección con su punto: la coordenada se escribe a mano hasta que el núcleo geocodifique. */
export const Mapa = ({ campo, ruta, soloLectura }: Props) => {
  const { valor, cambiar, error } = useValor<{ direccion?: string; lat?: number | string; lng?: number | string }>(ruta)
  const actual = valor ?? {}
  const poner = (cambios: Partial<typeof actual>) => cambiar({ ...actual, ...cambios })
  return (
    <Envoltura campo={campo} error={error} ruta={ruta}>
      <div className="grid gap-2 sm:grid-cols-4" aria-describedby={describe(ruta, campo, error)}>
        <input aria-label="Dirección" className={clases(claseDeEntrada(error), 'sm:col-span-2')} disabled={soloLectura} id={ruta} onChange={(e) => poner({ direccion: e.target.value })} placeholder="Dirección" value={texto(actual.direccion)} />
        <input aria-label="Latitud" className={claseDeEntrada(error)} disabled={soloLectura} inputMode="decimal" onChange={(e) => poner({ lat: e.target.value })} placeholder="Latitud (-12.04)" value={actual.lat ?? ''} />
        <input aria-label="Longitud" className={claseDeEntrada(error)} disabled={soloLectura} inputMode="decimal" onChange={(e) => poner({ lng: e.target.value })} placeholder="Longitud (-77.03)" value={actual.lng ?? ''} />
      </div>
    </Envoltura>
  )
}

/** La plantilla del tema con la que se pinta una página: un identificador, con «estandar» a mano. */
export const PlantillaDePagina = ({ campo, ruta, soloLectura }: Props) => {
  const { valor, cambiar, error } = useValor<string>(ruta)
  const lista = `${ruta}-plantillas`
  const conocidas = [...new Set(['estandar', valor ?? ''].filter(Boolean))]
  return (
    <Envoltura campo={campo} error={error} ruta={ruta}>
      <input aria-describedby={describe(ruta, campo, error)} aria-invalid={Boolean(error)} className={clases(claseDeEntrada(error), 'font-mono text-[13px]')} disabled={soloLectura} id={ruta} list={lista} onChange={(e) => cambiar(e.target.value)} value={valor ?? ''} />
      <datalist id={lista}>
        {conocidas.map((p) => (
          <option key={p} value={p} />
        ))}
      </datalist>
    </Envoltura>
  )
}

type Opcion = { valor: string; etiqueta: string }

/**
 * Los tipos de contenido y las taxonomías de la web (con sus borradores), y los términos de
 * cada taxonomía, por su identificador: es lo que guardan una regla de ubicación
 * («el tipo de contenido es…») y el destino de una relación. Se eligen por su nombre; nadie
 * tiene que saberse el identificador.
 */
const useEstructuraDeLaWeb = (sitio: null | number, que: { tipos?: boolean; taxonomias?: boolean; terminos?: boolean }) => {
  const tipos = useQuery({
    queryKey: ['lista', 'tipos-de-contenido', 'para-elegir', sitio],
    enabled: Boolean(que.tipos) && sitio !== null,
    staleTime: 60_000,
    queryFn: () => pedir<Lista<{ id: number; slug?: null | string; nombrePlural: string }>>(`/tipos-de-contenido${consulta({ 'where[sitio][equals]': sitio ?? 0, limit: 100, depth: 0, sort: 'posicion', draft: true })}`),
  })
  const taxonomias = useQuery({
    queryKey: ['lista', 'taxonomias', 'para-elegir', sitio],
    enabled: Boolean(que.taxonomias || que.terminos) && sitio !== null,
    staleTime: 60_000,
    queryFn: () => pedir<Lista<{ id: number; slug?: null | string; nombrePlural: string }>>(`/taxonomias${consulta({ 'where[sitio][equals]': sitio ?? 0, limit: 100, depth: 0, sort: 'posicion', draft: true })}`),
  })
  const terminos = useQuery({
    queryKey: ['lista', 'terminos', 'para-elegir', sitio],
    enabled: Boolean(que.terminos) && sitio !== null,
    staleTime: 60_000,
    queryFn: () => pedir<Lista<{ id: number; slug?: null | string; nombre: string; taxonomia: number | { id: number } }>>(`/terminos${consulta({ 'where[sitio][equals]': sitio ?? 0, limit: 500, depth: 0, sort: 'nombre' })}`),
  })
  const porSlug = (docs: Array<{ slug?: null | string; nombrePlural: string }> | undefined): Opcion[] =>
    (docs ?? []).filter((d) => d.slug).map((d) => ({ valor: d.slug as string, etiqueta: d.nombrePlural }))
  const taxonomiaDe = new Map((taxonomias.data?.docs ?? []).map((t) => [t.id, t]))
  const deTerminos: Opcion[] = (terminos.data?.docs ?? []).flatMap((t) => {
    const tax = taxonomiaDe.get(typeof t.taxonomia === 'number' ? t.taxonomia : t.taxonomia.id)
    return tax?.slug && t.slug ? [{ valor: `${tax.slug}:${t.slug}`, etiqueta: `${tax.nombrePlural} › ${t.nombre}` }] : []
  })
  return { tipos: porSlug(tipos.data?.docs), taxonomias: porSlug(taxonomias.data?.docs), terminos: deTerminos.sort((a, b) => a.etiqueta.localeCompare(b.etiqueta, 'es')) }
}

/** Un desplegable de opciones que conserva un valor que ya no está entre ellas (se guardó antes, o se borró). */
const DesplegableConservador = ({ campo, ruta, soloLectura, opciones, vacio = 'Elige…' }: Props & { opciones: Opcion[]; vacio?: string }) => {
  const { valor, cambiar, error } = useValor<string>(ruta)
  const actual = valor ?? ''
  return (
    <Envoltura campo={campo} error={error} ruta={ruta}>
      <Selector aria-describedby={describe(ruta, campo, error)} aria-invalid={Boolean(error)} disabled={soloLectura} id={ruta} onChange={(e) => cambiar(e.target.value || undefined)} value={actual}>
        <option value="">{vacio}</option>
        {opciones.map((o) => (
          <option key={o.valor} value={o.valor}>
            {o.etiqueta}
          </option>
        ))}
        {actual && !opciones.some((o) => o.valor === actual) && <option value={actual}>«{actual}» (ya no existe en esta web)</option>}
      </Selector>
    </Envoltura>
  )
}

/**
 * Un tipo de contenido o una taxonomía de la web (`custom.panel.coleccion`): a lo que apunta
 * una relación de un grupo de campos, o de dónde sale una lista de una plantilla. Un
 * desplegable con los de la web, que guarda su identificador; `vacio` dice qué significa no
 * elegir ninguno. `tipo-de-contenido` es lo mismo con los tipos.
 */
export const DeLaEstructura = ({ campo, ruta, soloLectura }: Props) => {
  const { sitio } = useFormulario()
  const deTaxonomias = campo.control?.coleccion === 'taxonomias'
  const { tipos, taxonomias } = useEstructuraDeLaWeb(sitio, { tipos: !deTaxonomias, taxonomias: deTaxonomias })
  const vacio = typeof campo.control?.vacio === 'string' ? campo.control.vacio : deTaxonomias ? 'Elige una taxonomía…' : 'Elige un tipo…'
  return <DesplegableConservador campo={campo} opciones={deTaxonomias ? taxonomias : tipos} ruta={ruta} soloLectura={soloLectura} vacio={vacio} />
}

/** De qué colección salen las categorías que mira cada regla. */
const CATEGORIAS_DE: Record<string, string> = { 'entradas.categoria': 'categorias', 'productos.categoria': 'categorias', 'servicios.categoria': 'categorias-de-servicios' }

/**
 * El valor de una regla de ubicación de un grupo de campos, según lo que mire la regla (el
 * campo de al lado, `hermano`): las entidades con sus nombres (los da el núcleo en el
 * control), las categorías de la web, la plantilla, o un texto.
 */
export const ValorDeUbicacion = ({ campo, ruta, soloLectura }: Props) => {
  const { valores, sitio } = useFormulario()
  const { valor, cambiar, error } = useValor<string>(ruta)
  const fila = (leer(valores, ruta.split('.').slice(0, -1).join('.')) ?? {}) as Fila
  const que = texto(fila[texto(campo.control?.hermano, 'entidad')])
  const entidades = Array.isArray(campo.control?.entidades) ? (campo.control.entidades as Array<{ valor: string; etiqueta: string }>) : []
  const coleccionDeCategorias = CATEGORIAS_DE[que]
  const estructura = useEstructuraDeLaWeb(sitio, { tipos: que === 'contenidos.tipo', taxonomias: que === 'terminos.taxonomia', terminos: que.endsWith('.termino') })
  const { data: categorias } = useQuery({
    queryKey: ['categorias-para-ubicar', coleccionDeCategorias, sitio],
    queryFn: () => pedir<Lista<{ id: number; nombre: string }>>(`/${coleccionDeCategorias}${consulta({ 'where[sitio][equals]': sitio ?? 0, limit: 200, depth: 0, sort: 'nombre' })}`),
    enabled: Boolean(coleccionDeCategorias) && sitio !== null,
    staleTime: 60_000,
  })
  const actual = valor ?? ''
  const opciones =
    que === 'entidad'
      ? entidades
      : coleccionDeCategorias
        ? (categorias?.docs ?? []).map((c) => ({ valor: String(c.id), etiqueta: c.nombre }))
        : que === 'contenidos.tipo'
          ? estructura.tipos
          : que === 'terminos.taxonomia'
            ? estructura.taxonomias
            : que.endsWith('.termino')
              ? estructura.terminos
              : null
  return (
    <Envoltura campo={campo} error={error} ruta={ruta}>
      {opciones ? (
        <Selector aria-describedby={describe(ruta, campo, error)} aria-invalid={Boolean(error)} disabled={soloLectura} id={ruta} onChange={(e) => cambiar(e.target.value)} value={actual}>
          <option value="">Elige…</option>
          {opciones.map((o) => (
            <option key={o.valor} value={o.valor}>
              {o.etiqueta}
            </option>
          ))}
          {actual && !opciones.some((o) => o.valor === actual) && <option value={actual}>«{actual}»</option>}
        </Selector>
      ) : (
        <input
          aria-describedby={describe(ruta, campo, error)}
          aria-invalid={Boolean(error)}
          className={clases(claseDeEntrada(error), 'font-mono text-[13px]')}
          disabled={soloLectura}
          id={ruta}
          onChange={(e) => cambiar(e.target.value)}
          placeholder={que === 'paginas.plantilla' ? 'estandar' : que.endsWith('.termino') ? 'taxonomia:termino' : 'identificador'}
          value={actual}
        />
      )}
    </Envoltura>
  )
}
